import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINT } from "../../../../services/api/resources";
import classes from './charts.module.css';
import TablePagination from "./Pagination";
import HeaderTitle from "../../../../components/common/HeaderTitle/HeaderTitle";

const getStatusText = (isApproved, is_account_approved, isFinApproved) => {
  if (
    isApproved == "1" &&
    is_account_approved == "1" &&
    isFinApproved == "1"
  ) {
    return "Approved";
  } else if (isApproved === "3") {
    return "Rejected";
  } else {
    return "Pending";
  }
};

const InvoiceStatusTable = () => {
  const [vendorInvoiceData, setVendorInvoiceData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);

  const rowsPerPage = 10;

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_ENDPOINT.get_all_invoices);
        setVendorInvoiceData(res.data?.invoices || []);
      } catch (error) {
        console.error("Error fetching invoice data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);


  const handleSort = (key) => {
    setSortConfig((prev) => {
      const direction = prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  const filteredData = vendorInvoiceData.filter((invoice) => {
    const nameMatch = invoice.vendor_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const invoiceMatch = invoice.vendor_code
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const status = getStatusText(
      invoice.is_approved,
      invoice.is_account_approved,
      invoice.is_fin_approved
    );

    const statusMatch = statusFilter === "All" || status === statusFilter;

    return (nameMatch || invoiceMatch) && statusMatch;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const valA = a[sortConfig.key];
    const valB = b[sortConfig.key];

    if (sortConfig.key === "status") {
      const statusA = getStatusText(a.is_approved, a.is_account_approved, a.is_fin_approved);
      const statusB = getStatusText(b.is_approved, b.is_account_approved, b.is_fin_approved);
      return sortConfig.direction === "asc"
        ? statusA.localeCompare(statusB)
        : statusB.localeCompare(statusA);
    }

    if (typeof valA === "string") {
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    } else if (typeof valA === "number") {
      return sortConfig.direction === "asc" ? valA - valB : valB - valA;
    }

    return 0;
  });

  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  return (
    <div>
      <HeaderTitle titleText={"Invoice Status"} />

      {/* Filters */}
      <div className={classes.filterWrapper}>
        <input
          type="text"
          placeholder="Search by name or invoice no..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className={classes.searchInput}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          className={classes.filterSelect}
        >
          <option value="All">All Status</option>
          <option value="Approved">Approved</option>
          <option value="Pending">Pending</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <table className={classes.statustable}>
        <thead>
          <tr>
            <th onClick={() => handleSort("vendor_code")}>
              Invoice No. {sortConfig.key === "vendor_code" && (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th onClick={() => handleSort("vendor_name")}>
              Merchant Name {sortConfig.key === "vendor_name" && (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th onClick={() => handleSort("amount")}>
              Amount {sortConfig.key === "amount" && (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th onClick={() => handleSort("status")}>
              Status {sortConfig.key === "status" && (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th>Signed On</th>
            <th>Approved By</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7" className={classes.loadingRow}>Loading data...</td>
            </tr>
          ) : currentItems.length === 0 ? (
            <tr>
              <td colSpan="7" className="no-data">No data found</td>
            </tr>
          ) : (
            currentItems.map((invoice) => {
              const status = getStatusText(invoice.is_approved, invoice.is_account_approved, invoice.is_fin_approved);
              return (
                <tr key={invoice.id}>
                  <td>{invoice.vendor_code}</td>
                  <td>{invoice.vendor_name}</td>
                  <td>₹{parseInt(invoice.amount).toLocaleString()}</td>
                  <td>{status}</td>
                  <td>{invoice.esign_status === "Signed" ? new Date(invoice.created_on).toLocaleDateString() : "-"}</td>
                  <td>{invoice.account_approved === true ? "Finance Team" : "-"}</td>
                  <td>
                    <button
                      className="action-btn"
                      onClick={() => {
                        setPdfBase64(invoice.pdf);
                        setPdfModalOpen(true);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {filteredData.length > rowsPerPage && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredData.length}
        />
      )}

      {/* PDF Modal */}
      {pdfModalOpen && (
        <div className={classes.pdfmodaloverlay} onClick={() => setPdfModalOpen(false)}>
          <div className={classes.pdfmodalcontent} onClick={(e) => e.stopPropagation()}>
            <button className={classes.closebtn} onClick={() => setPdfModalOpen(false)}>X</button>
            {pdfBase64 ? (
              <iframe
                title="Invoice PDF"
                src={`data:application/pdf;base64,${pdfBase64}`}
                width="100%"
                height="600px"
                style={{ border: "none" }}
              />
            ) : (
              <p>PDF not available</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceStatusTable;
